import { useTranslate } from '@ur-solutions/react-hooks'
import xor from 'lodash/xor'
import { type CSSProperties, useState } from 'react'
import { FAIcon } from '@/components/fa-icon'
import { Button, Popover, PopoverTrigger } from '@/components/ui'
import { cn } from '@/lib/utils'
import { useAllIssueTags } from '@/modules/common/hooks/use-all-issue-tags'
import { AddTagButton } from './add-tag-button'
import { TagChip } from './tag-chip'
import { TagListPopover } from './tag-list-popover'
import { useIssueTagCreate } from './use-tag-create'

interface Props {
  value: string[]
  onChange(next: string[]): void
  placeholder?: string
  disabled?: boolean
  className?: string
  style?: CSSProperties
}

export const IssueTagPicker: React.FC<Props> = ({
  value,
  onChange,
  placeholder,
  disabled,
  className,
  style,
}) => {
  const translations = useTranslate({
    placeholder: 'issues.tag-picker.placeholder',
    search: 'issues.tag-picker.search',
    noResults: 'issues.tag-picker.no-results',
    addTag: 'settings.add-issue-tag',
    tags: 'common.tags',
  })

  const { tags } = useAllIssueTags()
  const selected = tags.filter(t => value.includes(t.id))

  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const { prompt: openCreatePrompt } = useIssueTagCreate(tags)

  function toggle(id: string) {
    onChange(xor(value, [id]))
  }

  async function handleAddTag() {
    setOpen(false)
    const newId = await openCreatePrompt()
    if (newId) onChange([...value, newId])
  }

  return (
    <div className="flex flex-col gap-2 w-full min-w-0">
      <h2>{translations.tags}</h2>
      <Popover open={open} onOpenChange={setOpen} modal={false}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              'justify-start w-full min-w-0 py-1 px-3 bg-quaternary border-gray-200',
              'flex flex-wrap gap-1 h-auto! min-h-[3rem] whitespace-normal',
              className
            )}
            style={style}
          >
            {selected.length === 0 ? (
              <span className="text-gray-400">
                {placeholder ?? translations.placeholder}
              </span>
            ) : (
              selected.map(tag => (
                <TagChip
                  key={tag.id}
                  tag={tag}
                  onRemove={() => toggle(tag.id)}
                />
              ))
            )}
            <AddTagButton
              label={translations.addTag}
              onActivate={() => void handleAddTag()}
            />
            <FAIcon
              className="text-gray-300"
              icon={open ? 'chevron-up' : 'chevron-down'}
            />
          </Button>
        </PopoverTrigger>

        <TagListPopover
          tags={tags}
          selectedIds={value}
          search={search}
          searchPlaceholder={translations.search}
          noResultsLabel={translations.noResults}
          onSearchChange={setSearch}
          onToggle={toggle}
        />
      </Popover>
    </div>
  )
}
